/**
 * Proposal Review Card Component
 * 
 * Complete redesign with:
 * - All proposal stats displayed (questions, confidence, citations, gaps, views, downloads)
 * - Proper deadline extraction and display (no "No deadline" when missing)
 * - ALWAYS VISIBLE status change buttons
 * - Optimized layout without wasted space
 * - All report metadata shown
 * - Proper navigation to ProposalManagementDashboard
 */

import React, { useState } from 'react';
import { Card, Badge, Button, Modal } from '@tracekit/core';
import { AggregatedReview } from '../types/review';
import { ShareReportModal } from './ShareReportModal';
import { 
  CheckCircle, 
  Clock, 
  FileCheck, 
  Archive, 
  MessageSquare,
  Calendar,
  User,
  Building2,
  ChevronRight,
  Share2,
  XCircle,
  FolderOpen,
  Eye,
  Download,
  AlertTriangle,
  TrendingUp,
  FileText,
  ClipboardCheck,
} from 'lucide-react';

interface ProposalReviewCardProps {
  review: AggregatedReview;
  onClick?: (reportId: string) => void;
  onStatusChange?: (reportId: string, newStatus: string) => void;
  canChangeStatus?: boolean;
  isReviewer?: boolean;
}

export const ProposalReviewCard: React.FC<ProposalReviewCardProps> = ({
  review,
  onClick,
  onStatusChange,
  canChangeStatus = true,
  isReviewer = false, // Default to false (admin) - reviewers should be explicitly set
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState<string | null>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case 'approved':
      case 'submitted':
        return 'success';
      case 'in_review':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Reviewer-appropriate status actions
  // For admins (isReviewer=false): Can do all actions
  // For reviewers (isReviewer=true): Can only approve/reject, not submit for review
  const getNextStatus = (currentStatus: string): { status: string; label: string; icon: any; colorClass: string } | null => {
    switch (currentStatus) {
      case 'draft':
        // Only admins can submit for review
        if (isReviewer) return null;
        return { status: 'in_review', label: 'Submit for Review', icon: Clock, colorClass: 'border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20' };
      case 'in_review':
        // Both admins and reviewers can approve
        return { status: 'approved', label: 'Approve', icon: CheckCircle, colorClass: 'border-green-300 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' };
      case 'approved':
        // Only admins can mark as submitted
        if (isReviewer) return null;
        return { status: 'submitted', label: 'Mark as Submitted', icon: FileCheck, colorClass: 'border-purple-300 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20' };
      case 'submitted':
        // Only admins can archive
        if (isReviewer) return null;
        return { status: 'archived', label: 'Archive', icon: Archive, colorClass: 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/20' };
      case 'rejected':
        // Can return rejected to draft (admin only)
        if (isReviewer) return null;
        return { status: 'draft', label: 'Return to Draft', icon: XCircle, colorClass: 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/20' };
      default:
        return null;
    }
  };

  const getPreviousStatus = (currentStatus: string): { status: string; label: string; icon: any; colorClass: string } | null => {
    switch (currentStatus) {
      case 'in_review':
        return { status: 'draft', label: 'Return to Draft', icon: XCircle, colorClass: 'text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/20' };
      case 'approved':
        return { status: 'in_review', label: 'Return to Review', icon: Clock, colorClass: 'text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' };
      default:
        return null;
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (!review.reportId) {
      console.error('[ProposalReviewCard] Cannot change status: reportId is missing');
      return;
    }
    if (onStatusChange) {
      onStatusChange(review.reportId, newStatus);
    }
    setShowStatusConfirm(null);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[data-no-click]') ||
      target.closest('.share-button') ||
      !review.reportId
    ) {
      return;
    }
    // Navigate to ProposalManagementDashboard with proposal selected
    window.location.href = `/rfp/proposals-management?tab=list&proposal_id=${review.reportId}`;
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (review.reportId) {
      // Navigate to ProposalManagementDashboard with proposal selected
      window.location.href = `/rfp/proposals-management?tab=list&proposal_id=${review.reportId}`;
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareModal(true);
  };

  const nextStatus = getNextStatus(review.status);
  const previousStatus = getPreviousStatus(review.status);
  const NextIcon = nextStatus?.icon;
  const PreviousIcon = previousStatus?.icon;

  // Calculate progress percentages
  const questionsProgress = review.totalQuestions && review.totalQuestions > 0
    ? Math.round((review.answeredQuestions || 0) / review.totalQuestions * 100)
    : 0;
  const confidencePercent = review.avgConfidence ? Math.round(review.avgConfidence * 100) : 0;

  // Display hierarchy - ensure no duplication
  // Use name from report if available, fallback to proposalTitle, then clientName
  const displayTitle = review.name || review.proposalTitle || review.clientName || 'Untitled Proposal';
  const displaySubtitle = review.projectName || (review.clientName && review.name ? review.clientName : null);
  const showClientAsSecondary = review.projectName && review.clientName && review.name;

  // Format deadline - only show if it exists
  const deadlineDate = review.deadline ? formatDate(review.deadline) : null;

  return (
    <>
      <div
        className="cursor-pointer"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick(e as any);
          }
        }}
        aria-label={`View proposal: ${displayTitle}`}
      >
      <Card 
        className="p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.01] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
      >
        <div className="flex items-start gap-4">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header Row - Single line with title and client */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-theme-text mb-1 line-clamp-2">
                  {displayTitle}
                </h3>
                <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
                  {review.projectName && (
                    <>
                      <FolderOpen className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{review.projectName}</span>
                      {showClientAsSecondary && (
                        <>
                          <span className="text-gray-400">•</span>
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{review.clientName}</span>
                        </>
                      )}
                    </>
                  )}
                  {!review.projectName && (
                    <>
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{review.clientName}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShareClick}
                  className="share-button"
                  data-no-click
                  title="Share report"
                  aria-label={`Share proposal: ${displayTitle}`}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <button
                  onClick={handleChevronClick}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  data-no-click
                  aria-label="View proposal details"
                  title="View proposal details"
                >
                  <ChevronRight className="w-5 h-5 text-theme-text-secondary flex-shrink-0" />
                </button>
              </div>
            </div>

            {/* Status and Quick Actions Row - ALWAYS VISIBLE */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Badge variant={getStatusBadgeVariant(review.status)}>
                {review.status === 'draft' ? 'Draft' :
                 review.status === 'in_review' ? 'In Review' :
                 review.status === 'approved' ? 'Approved' :
                 review.status === 'submitted' ? 'Submitted' :
                 review.status === 'archived' ? 'Archived' :
                 review.status}
              </Badge>
              
              {/* QA Completion Badge - Show if QA data exists */}
              {(review.qa_writer_checklist_completion !== undefined || review.qa_reviewer_checklist_completion !== undefined) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (review.reportId) {
                      window.location.href = `/rfp/proposals-management?tab=qa&proposal_id=${review.reportId}`;
                    }
                  }}
                  data-no-click
                  className="flex items-center gap-1"
                  title="View QA checklist"
                >
                  <Badge 
                    variant={
                      review.qa_overall_ready 
                        ? 'success' 
                        : (review.qa_writer_checklist_completion || 0) >= 50 || (review.qa_reviewer_checklist_completion || 0) >= 50
                        ? 'warning'
                        : 'error'
                    }
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <ClipboardCheck className="w-3 h-3 mr-1" />
                    QA: {review.qa_writer_checklist_completion !== undefined 
                      ? `${Math.round(review.qa_writer_checklist_completion)}%` 
                      : review.qa_reviewer_checklist_completion !== undefined
                      ? `${Math.round(review.qa_reviewer_checklist_completion)}%`
                      : '0%'}
                  </Badge>
                </button>
              )}

              {/* Status Change Buttons - ALWAYS VISIBLE when canChangeStatus is true */}
              {canChangeStatus && (
                <div className="flex items-center gap-2 flex-wrap">
                  {nextStatus && NextIcon && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowStatusConfirm(nextStatus.status);
                      }}
                      data-no-click
                      className={`${nextStatus.colorClass} flex-shrink-0 min-w-[140px]`}
                      aria-label={`Change status to ${nextStatus.status}`}
                    >
                      <NextIcon className="w-4 h-4 mr-1" aria-hidden="true" />
                      {nextStatus.label}
                    </Button>
                  )}
                  {previousStatus && PreviousIcon && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowStatusConfirm(previousStatus.status);
                      }}
                      data-no-click
                      className={`${previousStatus.colorClass} flex-shrink-0 min-w-[140px]`}
                      aria-label={`Change status to ${previousStatus.status}`}
                    >
                      <PreviousIcon className="w-4 h-4 mr-1" aria-hidden="true" />
                      {previousStatus.label}
                    </Button>
                  )}
                  {/* Show message if no status actions available */}
                  {!nextStatus && !previousStatus && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                      No status actions available
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Stats Grid - Only show if we have stats */}
            {(review.totalQuestions !== undefined || 
              review.avgConfidence !== undefined || 
              review.totalCitations !== undefined || 
              review.evidenceGaps !== undefined) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {/* Questions Progress */}
                {review.totalQuestions !== undefined && review.totalQuestions > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-medium text-blue-900 dark:text-blue-300">Questions</span>
                    </div>
                    <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      {review.answeredQuestions || 0}/{review.totalQuestions}
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-1.5 mt-1">
                      <div 
                        className="bg-blue-600 dark:bg-blue-400 h-1.5 rounded-full transition-all"
                        style={{ width: `${questionsProgress}%` }}
                      />
                    </div>
                    <div className="text-xs text-blue-700 dark:text-blue-400 mt-1">{questionsProgress}% complete</div>
                  </div>
                )}

                {/* Confidence Score */}
                {review.avgConfidence !== undefined && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-xs font-medium text-green-900 dark:text-green-300">Confidence</span>
                    </div>
                    <div className="text-lg font-semibold text-green-900 dark:text-green-100">
                      {confidencePercent}%
                    </div>
                    <div className="w-full bg-green-200 dark:bg-green-800 rounded-full h-1.5 mt-1">
                      <div 
                        className="bg-green-600 dark:bg-green-400 h-1.5 rounded-full transition-all"
                        style={{ width: `${confidencePercent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Citations */}
                {review.totalCitations !== undefined && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <span className="text-xs font-medium text-purple-900 dark:text-purple-300">Citations</span>
                    </div>
                    <div className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                      {review.totalCitations}
                    </div>
                  </div>
                )}

                {/* Evidence Gaps */}
                {review.evidenceGaps !== undefined && (
                  <div className={`rounded-lg p-3 border ${
                    review.evidenceGaps > 0 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {review.evidenceGaps > 0 ? (
                        <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                      <span className={`text-xs font-medium ${
                        review.evidenceGaps > 0
                          ? 'text-red-900 dark:text-red-300'
                          : 'text-gray-900 dark:text-gray-300'
                      }`}>Gaps</span>
                    </div>
                    <div className={`text-lg font-semibold ${
                      review.evidenceGaps > 0
                        ? 'text-red-900 dark:text-red-100'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {review.evidenceGaps}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer Metadata */}
            <div className="flex items-center gap-4 text-xs text-theme-text-secondary pt-3 border-t border-gray-200 dark:border-gray-700 flex-wrap">
              {review.currentReviewer && (
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>{review.currentReviewer}</span>
                </div>
              )}
              {/* Only show comments if count > 0 */}
              {review.commentsCount && review.commentsCount > 0 && (
                <div className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  <span>{review.commentsCount} comment{review.commentsCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              {deadlineDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Due: {deadlineDate}</span>
                </div>
              )}
              {review.viewCount !== undefined && review.viewCount > 0 && (
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  <span>{review.viewCount} view{review.viewCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              {review.downloadCount !== undefined && review.downloadCount > 0 && (
                <div className="flex items-center gap-1">
                  <Download className="w-3 h-3" />
                  <span>{review.downloadCount} download{review.downloadCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="ml-auto">
                Updated: {formatDate(review.lastUpdated) || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </Card>
      </div>

      {/* Share Modal */}
      <ShareReportModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        reportId={review.reportId}
        reportName={displayTitle}
      />

      {/* Status Change Confirmation Modal */}
      {showStatusConfirm && (
        <Modal
          isOpen={!!showStatusConfirm}
          onClose={() => setShowStatusConfirm(null)}
          title="Confirm Status Change"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to change the status from{' '}
              <strong className="text-gray-900 dark:text-gray-100">{review.status}</strong> to{' '}
              <strong className="text-gray-900 dark:text-gray-100">{showStatusConfirm}</strong>?
            </p>
            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowStatusConfirm(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleStatusChange(showStatusConfirm)}
                variant="primary"
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
